import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { box, sphere, combine } from '../../engine/geo/primitives';
import { translate, setEmissive, MeshData } from '../../engine/geo/mesh';
import type { BoxCollider } from '../../engine/physics/StaticWorld';
import type { Ctx } from '../Ctx';
import { PALETTE, BiomeId } from './Layout';
import type { Keep } from './Keeps';

/** 5x7 dot-matrix glyphs for the floating-sphere name. */
const GLYPHS: Record<string, string[]> = {
  A: ['.###.', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  D: ['####.', '#...#', '#...#', '#...#', '#...#', '#...#', '####.'],
  E: ['#####', '#....', '#....', '####.', '#....', '#....', '#####'],
  H: ['#...#', '#...#', '#...#', '#####', '#...#', '#...#', '#...#'],
  J: ['..###', '...#.', '...#.', '...#.', '...#.', '#..#.', '.##..'],
  L: ['#....', '#....', '#....', '#....', '#....', '#....', '#####'],
  O: ['.###.', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  R: ['####.', '#...#', '#...#', '####.', '#.#..', '#..#.', '#...#'],
  S: ['.####', '#....', '#....', '.###.', '....#', '....#', '####.'],
  U: ['#...#', '#...#', '#...#', '#...#', '#...#', '#...#', '.###.'],
  W: ['#...#', '#...#', '#...#', '#.#.#', '#.#.#', '##.##', '#...#'],
  ' ': ['.....', '.....', '.....', '.....', '.....', '.....', '.....'],
};

export const AUTHOR = 'JOSHUA';

export interface SecretRoom {
  wall: BoxCollider;
  wallRd: { alpha: number; transparent: boolean };
  cube: Transform;
  letters: Transform;
  interior: AABB;
  opened: boolean;
}

/**
 * §9: the Robinett homage. A room behind the Gold Keep's east wall: glyph-covered walls, a cube of every
 * biome's palette rotating slowly, and the author's name spelled in floating spheres.
 */
export function buildSecretRoom(ctx: Ctx, keep: Keep): SecretRoom {
  const st = ctx.statics;
  const pos = keep.def.pos, hw = keep.def.w / 2, wall = keep.def.wall;
  const y0 = keep.floorY;
  // the east wall collider of the keep: find it by position and swap it for our own so we can disable it
  const eastX = pos.x + hw + wall / 2;
  const east = st.colliders.find((c) => c.kind === 'box' && Math.abs((c.aabb.min.x + c.aabb.max.x) / 2 - eastX) < 0.01 && c.aabb.min.z < pos.z && c.aabb.max.z > pos.z) as BoxCollider | undefined;
  if (east) east.enabled = false;
  // passable section of the east wall (3 m wide) gets its own collider + mesh
  const wallC = st.addBox(AABB.fromMinMax(eastX - wall / 2, y0 - 1, pos.z - 1.6, eastX + wall / 2, y0 + keep.def.h, pos.z + 1.6), 'secretWall');
  // the rest of the east wall stays solid
  st.addBox(AABB.fromMinMax(eastX - wall / 2, y0 - 1, pos.z - keep.def.d / 2 - wall, eastX + wall / 2, y0 + keep.def.h, pos.z - 1.6), 'keep');
  st.addBox(AABB.fromMinMax(eastX - wall / 2, y0 - 1, pos.z + 1.6, eastX + wall / 2, y0 + keep.def.h, pos.z + keep.def.d / 2 + wall), 'keep');
  const wallMesh = box(wall, keep.def.h, 3.2, keep.def.color);
  const wt = new Transform(); wt.position.set(eastX, y0 + keep.def.h / 2, pos.z); wt.snapPrev();
  const we = ctx.spawn('secret.wall', wt, wallMesh);
  const wallRd = ctx.renderables.req(we);

  // the room: 8 x 6 x 6 m east of the keep, sharing the wall
  const rx0 = eastX + wall / 2, rx1 = rx0 + 8, rz0 = pos.z - 3, rz1 = pos.z + 3, rh = 6;
  const parts: MeshData[] = [];
  const dark: [number, number, number] = [0.12, 0.1, 0.14];
  parts.push(translate(box(8, 0.3, 6, dark), (rx0 + rx1) / 2, y0 - 0.15, pos.z));
  parts.push(translate(box(8, 0.3, 6, dark), (rx0 + rx1) / 2, y0 + rh + 0.15, pos.z));
  parts.push(translate(box(8 + 1, rh + 0.6, 0.5, dark), (rx0 + rx1) / 2, y0 + rh / 2, rz0 - 0.25));
  parts.push(translate(box(8 + 1, rh + 0.6, 0.5, dark), (rx0 + rx1) / 2, y0 + rh / 2, rz1 + 0.25));
  parts.push(translate(box(0.5, rh + 0.6, 6, dark), rx1 + 0.25, y0 + rh / 2, pos.z));
  st.addBox(AABB.fromMinMax(rx0, y0 - 1, rz0 - 0.5, rx1 + 0.5, y0 + rh, rz0), 'secret');
  st.addBox(AABB.fromMinMax(rx0, y0 - 1, rz1, rx1 + 0.5, y0 + rh, rz1 + 0.5), 'secret');
  st.addBox(AABB.fromMinMax(rx1, y0 - 1, rz0, rx1 + 0.5, y0 + rh, rz1), 'secret');
  st.addBox(AABB.fromMinMax(rx0, y0 + rh, rz0, rx1, y0 + rh + 0.3, rz1), 'secret');
  // glyphs: little emissive tiles on the walls in a pseudo-random pattern
  let seed = 17;
  const rnd = () => { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
  const biomes: BiomeId[] = ['hearth', 'marsh', 'dunes', 'ironwood', 'cistern'];
  for (let i = 0; i < 90; i++) {
    const c = PALETTE[biomes[i % 5]][4];
    const tile = setEmissive(box(0.25, 0.25 + rnd() * 0.3, 0.06, c), c, 0.9);
    const side = rnd() < 0.5 ? rz0 : rz1;
    if (rnd() < 0.7) parts.push(translate(tile, rx0 + 0.5 + rnd() * 7, y0 + 0.5 + rnd() * (rh - 1), side + (side === rz0 ? 0.04 : -0.04)));
    else parts.push(translate(setEmissive(box(0.06, 0.25 + rnd() * 0.3, 0.25, c), c, 0.9), rx1 - 0.04, y0 + 0.5 + rnd() * (rh - 1), rz0 + 0.5 + rnd() * 5));
  }
  ctx.spawn('secret.room', new Transform(), combine(...parts));

  // the palette cube: each face a biome (six faces: five biomes + gold)
  const cube = new Transform();
  cube.position.set(rx0 + 5.5, y0 + 2.6, pos.z);
  cube.snapPrev();
  const faces: MeshData[] = [];
  const cols = [PALETTE.hearth[0], PALETTE.marsh[4], PALETTE.dunes[0], PALETTE.ironwood[4], PALETTE.cistern[4], [1, 0.8, 0.3] as [number, number, number]];
  const offs: [number, number, number][] = [[0.6, 0, 0], [-0.6, 0, 0], [0, 0.6, 0], [0, -0.6, 0], [0, 0, 0.6], [0, 0, -0.6]];
  cols.forEach((c, i) => faces.push(setEmissive(translate(box(i < 2 ? 0.1 : 1.1, i >= 2 && i < 4 ? 0.1 : 1.1, i >= 4 ? 0.1 : 1.1, c), ...offs[i]), c, 0.5)));
  faces.push(box(1.1, 1.1, 1.1, [0.2, 0.18, 0.22]));
  ctx.spawn('secret.cube', cube, combine(...faces));

  // the author's name in floating spheres
  const letters = new Transform();
  letters.position.set(rx0 + 2.0, y0 + 3.4, pos.z);
  letters.snapPrev();
  const dots: MeshData[] = [];
  const name = AUTHOR.toUpperCase();
  const w = name.length * 6 * 0.14;
  name.split('').forEach((ch, li) => {
    const gl = GLYPHS[ch] ?? GLYPHS[' '];
    gl.forEach((row, r) => row.split('').forEach((cell, c) => {
      if (cell !== '#') return;
      const z = -w / 2 + (li * 6 + c) * 0.14, y = (6 - r) * 0.14;
      dots.push(setEmissive(sphere(0.06, 6, 4, [1, 0.95, 0.8]), [1, 0.9, 0.6], 0.8));
      translate(dots[dots.length - 1], 0, y, z);
    }));
  });
  ctx.spawn('secret.name', letters, combine(...dots), { castShadow: false });

  return { wall: wallC, wallRd, cube, letters, interior: AABB.fromMinMax(rx0, y0 - 1, rz0, rx1, y0 + rh, rz1), opened: false };
}

/** Per step: the wall is passable while the Grey Dot is within 2 m of it; the cube turns. */
export function updateSecretRoom(room: SecretRoom, dotPos: Vec3 | null, playerPos: Vec3, time: number, dt: number): boolean {
  const c = room.wall.aabb;
  const near = !!dotPos && dotPos.x > c.min.x - 2 && dotPos.x < c.max.x + 2 && dotPos.z > c.min.z - 2 && dotPos.z < c.max.z + 2 && Math.abs(dotPos.y - c.min.y - 1) < 4;
  room.wall.enabled = !near;
  room.wallRd.alpha += ((near ? 0.25 : 1) - room.wallRd.alpha) * Math.min(1, dt * 6);
  room.wallRd.transparent = room.wallRd.alpha < 0.99;
  room.cube.rotation.setEuler(time * 0.4, Math.sin(time * 0.3) * 0.4, time * 0.17);
  room.letters.position.y += Math.sin(time * 1.1) * 0.0015;
  const inside = room.interior.containsPoint(playerPos);
  if (inside && !room.opened) { room.opened = true; return true; }
  return false;
}
